"use client";

import { useEffect, useRef, useState } from "react";
import { AttachPreviewImage } from "@/components/admin/attach-picker/attach-preview-image";
import { useTranslations } from "next-intl";
import { XIcon } from "lucide-react";

import { AttachPickTooltip } from "@/components/admin/attach-picker/attach-pick-tooltip";
import type { AttachFileRecord } from "@/components/admin/attach-picker/types";
import {
  formatAttachDimensions,
  formatFileSize,
  isImageMime,
} from "@/components/admin/attach-picker/utils";
import { AttachFileActions } from "./attach-file-actions";
import { AdminTableIconActionButton } from "@/components/admin/admin-table-icon-actions";
import { Progress } from "@/components/ui/progress";
import { HoverTip } from "@/components/ui/tooltip";
import type { AttachUploadTask } from "@/lib/upload/attach-upload-client";
import { cn } from "@/lib/utils";

const attachNameLabelClass =
  "box-border h-4 max-h-4 min-h-4 min-w-0 flex-1 truncate py-0 text-left text-xs leading-4 text-foreground";

function AttachListThumb({ file }: { file: Pick<AttachFileRecord, "url" | "thumbUrl" | "mimeType" | "originalName"> }) {
  const t = useTranslations("site.attach");
  const showImage = Boolean(file.url && isImageMime(file.mimeType));

  return (
    <div className="relative flex size-8 shrink-0 items-center justify-center overflow-hidden rounded border bg-muted/30">
      {showImage ? (
        <AttachPreviewImage src={file.thumbUrl ?? file.url} fallback={file.url} alt={file.originalName} />
      ) : (
        <span className="text-[9px] text-muted-foreground">{t("fileThumb")}</span>
      )}
    </div>
  );
}

function AttachListTitle({
  file,
  owned,
  disabled,
  onRename,
}: {
  file: AttachFileRecord;
  owned: boolean;
  disabled?: boolean;
  onRename?: (id: string, originalName: string) => void;
}) {
  const canRename = Boolean(owned && onRename && !disabled);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(file.originalName);
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(file.originalName);
  }, [file.originalName]);

  useEffect(() => {
    if (!editing || !renameInputRef.current) {
      return;
    }

    const input = renameInputRef.current;
    input.focus();
    const end = input.value.length;
    input.setSelectionRange(end, end);
  }, [editing]);

  function cancelRename() {
    setDraft(file.originalName);
    setEditing(false);
  }

  function commitRename() {
    if (!canRename || !onRename) {
      cancelRename();
      return;
    }

    const next = draft.trim();

    if (!next || next === file.originalName) {
      cancelRename();
      return;
    }

    onRename(file.id, next);
    setEditing(false);
  }

  if (canRename && editing) {
    return (
      <input
        autoComplete="off"
        ref={renameInputRef}
        value={draft}
        spellCheck={false}
        className={cn(
          attachNameLabelClass,
          "cursor-text border-0 bg-transparent shadow-none outline-none focus-visible:ring-0",
        )}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commitRename}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commitRename();
          }

          if (event.key === "Escape") {
            event.preventDefault();
            cancelRename();
          }
        }}
        onClick={(event) => event.stopPropagation()}
      />
    );
  }

  return (
    <p
      className={cn(
        attachNameLabelClass,
        canRename && "cursor-text hover:text-primary",
      )}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => {
        if (!canRename) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        setEditing(true);
      }}
    >
      {file.originalName}
    </p>
  );
}

export function AttachFileList({
  items,
  uploadTasks = [],
  selectedIds,
  pickedId,
  onToggleSelect,
  onPick,
  onDelete,
  onRename,
  onReplace,
  onMove,
  onCancelUpload,
  disabled,
}: {
  items: AttachFileRecord[];
  uploadTasks?: AttachUploadTask[];
  selectedIds: string[];
  pickedId: string | null;
  onToggleSelect: (id: string) => void;
  onPick: (file: AttachFileRecord) => void;
  onDelete: (id: string) => void;
  onRename?: (id: string, originalName: string) => void;
  onReplace?: (file: AttachFileRecord) => void;
  onMove: (file: AttachFileRecord) => void;
  onCancelUpload?: (taskId: string) => void;
  disabled?: boolean;
}) {
  const t = useTranslations("site.attach");
  const [actionsOpen, setActionsOpen] = useState<string | null>(null);

  return (
    <div className="min-w-0">
      <div className="grid grid-cols-[28px_32px_minmax(0,1fr)_72px_64px_52px] items-center gap-2 border-b px-1 py-2 text-xs text-muted-foreground">
        <span aria-hidden />
        <span>{t("colThumb")}</span>
        <span>{t("colTitle")}</span>
        <span className="text-center">{t("colDimensions")}</span>
        <span className="text-center">{t("colSize")}</span>
        <span className="text-center">{t("colActions")}</span>
      </div>
      <div className="divide-y divide-border/60">
        {uploadTasks.filter((task) => task.status !== "cancelled").map((task) => (
          <div key={task.id} className="grid grid-cols-[28px_32px_minmax(0,1fr)_72px_64px_52px] items-center gap-2 px-1 py-2 text-xs">
            <span aria-hidden />
            <AttachListThumb file={{ url: task.previewUrl, originalName: task.file.name, mimeType: task.file.type || null }} />
            <div className="min-w-0">
              <HoverTip content={task.file.name}><p className={attachNameLabelClass}>{task.file.name}</p></HoverTip>
              {task.status === "error" ? (
                <HoverTip content={task.error || t("uploadFailed")}><p role="status" className="mt-1 truncate text-destructive">{task.error || t("uploadFailed")}</p></HoverTip>
              ) : (
                <div className="mt-1 flex items-center gap-2">
                  <Progress value={task.progress} aria-label={task.file.name} trackClassName="h-1" />
                  <span className="shrink-0 text-muted-foreground tabular-nums">{task.phase === "saving" ? `${t("savingUpload")} ${task.progress}%` : `${task.progress}%`}</span>
                </div>
              )}
            </div>
            <span className="text-center text-muted-foreground">—</span>
            <span className="text-center text-muted-foreground tabular-nums">{formatFileSize(task.file.size)}</span>
            <div className="flex justify-center">
              {onCancelUpload ? <HoverTip content={task.status === "uploading" ? t("cancelUpload") : t("remove")}>
                <AdminTableIconActionButton label={task.status === "uploading" ? t("cancelUpload") : t("remove")} icon={XIcon} tone="destructive" onClick={() => onCancelUpload(task.id)} />
              </HoverTip> : null}
            </div>
          </div>
        ))}
        {items.map((file) => {
          const checked = selectedIds.includes(file.id);
          const picked = pickedId === file.id;
          const owned = file.owned !== false;

          return (
            <AttachPickTooltip key={file.id} url={file.url} disabled={actionsOpen !== null}>
              <div
                onClick={() => onPick(file)}
                className={cn(
                  "group/row grid grid-cols-[28px_32px_minmax(0,1fr)_72px_64px_52px] items-center gap-2 px-1 py-2 text-xs transition-colors",
                  "cursor-pointer hover:bg-muted/40",
                  picked && "bg-primary/5 ring-1 ring-inset ring-primary/20",
                  !picked && checked && "bg-muted/20",
                )}
              >
              <label
                className="flex justify-center"
                onClick={(event) => event.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  className="size-4 cursor-pointer rounded border-input"
                  onChange={() => onToggleSelect(file.id)}
                />
              </label>

              <AttachListThumb file={file} />

              <AttachListTitle
                file={file}
                owned={owned}
                disabled={disabled}
                onRename={onRename}
              />

              <span className="text-center text-muted-foreground tabular-nums">
                {formatAttachDimensions(file)}
              </span>

              <span className="text-center text-muted-foreground tabular-nums">
                {formatFileSize(file.fileSize)}
              </span>

              <div
                className="flex justify-center"
                onClick={(event) => event.stopPropagation()}
              >
                <AttachFileActions file={file} disabled={disabled} onOpenChange={(open) => setActionsOpen((current) => open ? file.id : current === file.id ? null : current)} onReplace={onReplace} onMove={onMove} onDelete={onDelete} />
              </div>
            </div>
            </AttachPickTooltip>
          );
        })}
      </div>
    </div>
  );
}
